// react and js
import React from "react";
import PropTypes from "prop-types";

// mui
import { Box, Typography, useTheme, useMediaQuery, Link } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import Fade from "@mui/material/Fade";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";

// hooks
import documentation from "../hooks/documentation";

// ----------------------------------------------------------------------
// Helper function to resolve documentation key with pattern matching
// ----------------------------------------------------------------------
function resolveDocumentationKey(helpReference, fieldName) {
  const fullKey = `${helpReference}.${fieldName}`;

  // First, try exact match
  if (documentation[fullKey] !== undefined) {
    return fullKey;
  }

  // Pattern matching for special cases
  const patterns = [
    {
      // HandleSymbolAndTimeframe.mapping_n_stringToReplace -> HandleSymbolAndTimeframe.String to replace
      regex: /^HandleSymbolAndTimeframe\.mapping_\d+_stringToReplace$/,
      targetKey: "HandleSymbolAndTimeframe.String to replace",
    },
    {
      // HandleSymbolAndTimeframe.mapping_n_replacementString -> HandleSymbolAndTimeframe.String it is replaced with
      regex: /^HandleSymbolAndTimeframe\.mapping_\d+_replacementString$/,
      targetKey: "HandleSymbolAndTimeframe.String it is replaced with",
    },
    {
      // HandleNews.filter_n_filterString -> HandleNews.Title name to filter
      regex: /^HandleNews\.filter_\d+_filterString$/,
      targetKey: "HandleNews.Title name to filter",
    },
  ];

  // Check each pattern using find method
  const matchedPattern = patterns.find((pattern) => pattern.regex.test(fullKey));

  if (matchedPattern && documentation[matchedPattern.targetKey] !== undefined) {
    return matchedPattern.targetKey;
  }

  // No match found
  return null;
}

// ----------------------------------------------------------------------
// Helper function to parse text with bold tags and line breaks
// ----------------------------------------------------------------------
function parseHelpText(text) {
  if (!text) return [];

  // Split by line breaks first
  const lines = text.split("\\n");

  return lines.map((line, lineIndex) => {
    if (line.trim() === "") {
      // Empty line
      return { type: "line", content: "", key: `line-${lineIndex}` };
    }

    // Parse bold tags within each line
    const parts = [];
    let currentIndex = 0;
    let partIndex = 0;

    // Find all <b> and </b> tags
    const boldRegex = /<b>(.*?)<\/b>/g;
    let match = boldRegex.exec(line);

    while (match !== null) {
      // Add text before the bold tag
      if (match.index > currentIndex) {
        const beforeText = line.substring(currentIndex, match.index);
        if (beforeText) {
          parts.push({
            type: "text",
            content: beforeText,
            key: `line-${lineIndex}-part-${partIndex}`,
          });
          partIndex += 1;
        }
      }

      // Add the bold text
      parts.push({
        type: "bold",
        content: match[1],
        key: `line-${lineIndex}-part-${partIndex}`,
      });
      partIndex += 1;

      currentIndex = match.index + match[0].length;
      match = boldRegex.exec(line);
    }

    // Add remaining text after the last bold tag
    if (currentIndex < line.length) {
      const remainingText = line.substring(currentIndex);
      if (remainingText) {
        parts.push({
          type: "text",
          content: remainingText,
          key: `line-${lineIndex}-part-${partIndex}`,
        });
      }
    }

    // If no bold tags found, treat the entire line as text
    if (parts.length === 0) {
      parts.push({
        type: "text",
        content: line,
        key: `line-${lineIndex}-part-0`,
      });
    }

    return { type: "line", content: parts, key: `line-${lineIndex}` };
  });
}

// ----------------------------------------------------------------------
// Helper component to render parsed help text
// ----------------------------------------------------------------------
function RenderHelpText({ parsedText }) {
  return (
    <div>
      {parsedText.map((line) => (
        <div key={line.key}>
          {line.content === "" ? (
            // Empty line for spacing
            <Typography variant="contentSmall" fontWeight={400}>
              &nbsp;
            </Typography>
          ) : (
            <Typography variant="contentSmall" fontWeight={400} component="div">
              {line.content.map((part) => {
                if (part.type === "bold") {
                  return (
                    <Typography key={part.key} variant="contentSmall" fontWeight={700} component="span">
                      {part.content}
                    </Typography>
                  );
                }
                return <span key={part.key}>{part.content}</span>;
              })}
            </Typography>
          )}
        </div>
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------
function SmartParameter({ value, helpReference, helpText, helpLink, fieldName, touched, errors, iconColor }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const hasError = fieldName && touched && errors && Boolean(touched[fieldName] && errors[fieldName]);

  // Resolve the documentation key (with pattern matching)
  const documentationKey = resolveDocumentationKey(helpReference, fieldName);

  let resolvedHelpText = helpText;
  let resolvedHelpLink = helpLink;

  if (documentationKey !== null) {
    const { helpText: docHelpText, helpLink: docHelpLink } = documentation[documentationKey];

    if (resolvedHelpText === "") {
      if (docHelpText !== undefined) {
        resolvedHelpText = docHelpText;
      }
    }
    if (resolvedHelpLink === "") {
      if (docHelpLink !== undefined) {
        resolvedHelpLink = docHelpLink;
      }
    }
  }

  // Parse the help text for bold formatting and line breaks
  const parsedHelpText = parseHelpText(resolvedHelpText);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {/* Show help text and error message */}
        {value && value.trim() !== "" ? (
          <Typography variant="contentSmallBold" sx={{ color: hasError ? "red" : "inherit" }}>
            {value}
            {hasError ? ` (${errors[fieldName]})` : ""}
          </Typography>
        ) : null}

        {/* Show tooltip only when helpText exists */}
        {resolvedHelpText && (
          <Tooltip
            title={<RenderHelpText parsedText={parsedHelpText} />}
            placement="top-start"
            followCursor
            TransitionComponent={Fade}
            TransitionProps={{ timeout: 600 }}
            enterDelay={200}
            leaveDelay={0}
            slotProps={{
              tooltip: {
                sx: {
                  maxWidth: isMobile ? "calc(75vw - 24px)" : "calc(50vw - 24px)",
                  whiteSpace: "pre-line",
                  background: "white",
                  border: "1px solid",
                  borderColor: "#232323",
                  p: 1,
                },
              },
            }}
          >
            {resolvedHelpLink && resolvedHelpLink.trim() !== "" ? (
              <Link href={resolvedHelpLink} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex", alignItems: "center" }}>
                <HelpOutlineOutlinedIcon sx={{ fontSize: "19px", mb: "-1px", height: "auto", color: hasError ? "red" : iconColor }} />
              </Link>
            ) : (
              <HelpOutlineOutlinedIcon sx={{ fontSize: "19px", mb: "-1px", height: "auto", color: hasError ? "red" : iconColor }} />
            )}
          </Tooltip>
        )}

        {/* Show clickable icon without tooltip when only helpLink exists (no helpText) */}
        {!resolvedHelpText && resolvedHelpLink && resolvedHelpLink.trim() !== "" && (
          <Link href={resolvedHelpLink} target="_blank" rel="noopener noreferrer" sx={{ display: "inline-flex", alignItems: "center" }}>
            <HelpOutlineOutlinedIcon sx={{ fontSize: "19px", mb: "-1px", height: "auto", color: hasError ? "red" : iconColor }} />
          </Link>
        )}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------------
// PropTypes
// ----------------------------------------------------------------------
SmartParameter.propTypes = {
  value: PropTypes.string,
  helpReference: PropTypes.string,
  helpText: PropTypes.string,
  helpLink: PropTypes.string,
  fieldName: PropTypes.string,
  touched: PropTypes.object,
  errors: PropTypes.object,
  iconColor: PropTypes.string,
};

// ----------------------------------------------------------------------
// Default Props
// ----------------------------------------------------------------------
SmartParameter.defaultProps = {
  value: "",
  helpReference: "",
  helpText: "",
  helpLink: "",
  fieldName: null,
  touched: null,
  errors: null,
  iconColor: "#2E2E2E",
};

// Add PropTypes for the helper component
RenderHelpText.propTypes = {
  parsedText: PropTypes.array.isRequired,
};

export default SmartParameter;
